import { useState, useEffect, useCallback } from "react";
import axios from "axios";

export default function useAccess() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");

  const [currentUser, setCurrentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("me") || "{}");
    } catch {
      return {};
    }
  });

  const [permissions, setPermissions] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const parseEmpRoles = useCallback((roles) => {
    try {
      const parsed = typeof roles === "string" ? JSON.parse(roles) : roles;
      return Array.isArray(parsed) ? parsed.map(Number) : [];
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "me") {
        try {
          setCurrentUser(JSON.parse(e.newValue || "{}"));
        } catch {
          setCurrentUser({});
        }
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const loadPermissions = useCallback(async ({ showLoading = true } = {}) => {
    if (!token) {
      setPermissions([]);
      setPermissionsLoaded(true);
      return;
    }

    if (showLoading) {
      setPermissionsLoaded(false);
    }

    try {
      const { data } = await axios.get(`${BASE_URL}/api/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const roleIds = parseEmpRoles(currentUser?.emp_roles);
      const filtered = Array.isArray(roleIds)
        ? data.filter((p) => roleIds.includes(Number(p.role_id)))
        : [];

      setPermissions(filtered);
    } catch (err) {
      console.error("Failed to load permissions:", err);
      setPermissions([]);
    } finally {
      setPermissionsLoaded(true);
    }
  }, [BASE_URL, token, currentUser, parseEmpRoles]);

  useEffect(() => {
    loadPermissions();
    const refresh = () => loadPermissions({ showLoading: false });
    window.addEventListener("permissionsUpdated", refresh);
    return () => window.removeEventListener("permissionsUpdated", refresh);
  }, [loadPermissions]);

  const hasPermission = useCallback(
    (pageName, resourceName, action) => {
      const acceptedActions =
        action === "create" || action === "assign"
          ? [action, "edit"]
          : action === "edit"
            ? ["edit", "create", "assign"]
            : [action];

      return permissions.some(
        (p) =>
          p.page_name === pageName &&
          p.resource_name === resourceName &&
          acceptedActions.includes(p.action),
      );
    },
    [permissions]
  );

  return {
    currentUser,
    permissions,
    permissionsLoaded,
    hasPermission,
    reloadPermissions: loadPermissions,
  };
}
